/**
 * Welcome Widgets - Clean Dashboard Components
 * 
 * Features:
 * - Lumen8 rainbow/ombre themed greeting
 * - Dynamic club name from REACT_APP_DEFAULT_CLUB
 * - Live team activity from Supabase presence
 * - Next match countdown with team badges
 * - Clean, consistent card layout
 */

import React, { useState, useEffect, useMemo } from 'react';

// ============================================================
// ANIMATIONS CSS
// ============================================================
const ANIMATIONS_CSS = `
@keyframes wdgtFadeIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes wdgtPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

@keyframes wdgtFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

@keyframes wdgtGlow {
  0%, 100% { box-shadow: 0 0 20px rgba(47, 214, 181, 0.2); }
  50% { box-shadow: 0 0 35px rgba(47, 214, 181, 0.4); }
}

@keyframes wdgtGradient {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes wdgtSlideIn {
  from { opacity: 0; transform: translateX(-12px); }
  to { opacity: 1; transform: translateX(0); }
}

.wdgt-fade { animation: wdgtFadeIn 0.4s ease-out forwards; }
.wdgt-delay-1 { animation-delay: 0.05s; opacity: 0; }
.wdgt-delay-2 { animation-delay: 0.1s; opacity: 0; }
.wdgt-delay-3 { animation-delay: 0.15s; opacity: 0; }
`;

// Inject CSS once
if (typeof document !== 'undefined' && !document.getElementById('wdgt-css-v2')) {
  const style = document.createElement('style');
  style.id = 'wdgt-css-v2';
  style.textContent = ANIMATIONS_CSS;
  document.head.appendChild(style);
}

// ============================================================
// THEME - Lumen8 Teal Ombre
// ============================================================
const LUMEN8_TEAL_GRADIENT = 'linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)';

const THEME = {
  accent: '#2fd6b5',
  accentAlt: '#84cc16',
  orange: '#f59e0b',
  red: '#ef4444',
  purple: '#a855f7',
  blue: '#3b82f6',
  textPrimary: '#ffffff',
  textSecondary: '#a1a1aa',
  textMuted: '#71717a',
  cardBg: 'rgba(24, 24, 32, 0.95)',
  cardBorder: 'rgba(255, 255, 255, 0.08)',
  glassBg: 'rgba(255, 255, 255, 0.04)',
};

// ============================================================
// AFL CLUB DATA
// ============================================================
const CLUBS = {
  STK: { name: 'St Kilda', short: 'Saints', color: '#ed1c24', textColor: '#fff', gradient: 'linear-gradient(135deg, #ed1c24, #000)' },
  CFC: { name: 'Carlton', short: 'Blues', color: '#0e1e2d', textColor: '#fff', gradient: 'linear-gradient(135deg, #0e1e2d, #1a3a5c)' },
  CAR: { name: 'Carlton', short: 'Blues', color: '#0e1e2d', textColor: '#fff', gradient: 'linear-gradient(135deg, #0e1e2d, #1a3a5c)' },
  COL: { name: 'Collingwood', short: 'Magpies', color: '#000000', textColor: '#fff', gradient: 'linear-gradient(135deg, #000, #333)' },
  ESS: { name: 'Essendon', short: 'Bombers', color: '#cc2031', textColor: '#fff', gradient: 'linear-gradient(135deg, #cc2031, #000)' },
  HAW: { name: 'Hawthorn', short: 'Hawks', color: '#4d2004', textColor: '#ffd204', gradient: 'linear-gradient(135deg, #4d2004, #ffd204)' },
  MEL: { name: 'Melbourne', short: 'Demons', color: '#0f1131', textColor: '#fff', gradient: 'linear-gradient(135deg, #0f1131, #ed1c24)' },
  RIC: { name: 'Richmond', short: 'Tigers', color: '#000000', textColor: '#ffd204', gradient: 'linear-gradient(135deg, #000, #ffd204)' },
  GEE: { name: 'Geelong', short: 'Cats', color: '#1c3c63', textColor: '#fff', gradient: 'linear-gradient(135deg, #1c3c63, #fff)' },
  WCE: { name: 'West Coast', short: 'Eagles', color: '#062ee2', textColor: '#ffd204', gradient: 'linear-gradient(135deg, #062ee2, #ffd204)' },
  FRE: { name: 'Fremantle', short: 'Dockers', color: '#2a1a54', textColor: '#fff', gradient: 'linear-gradient(135deg, #2a1a54, #fff)' },
  BRI: { name: 'Brisbane', short: 'Lions', color: '#a30046', textColor: '#fff', gradient: 'linear-gradient(135deg, #a30046, #0054a4)' },
  GWS: { name: 'GWS', short: 'Giants', color: '#f15c22', textColor: '#fff', gradient: 'linear-gradient(135deg, #f15c22, #000)' },
  SYD: { name: 'Sydney', short: 'Swans', color: '#ed171f', textColor: '#fff', gradient: 'linear-gradient(135deg, #ed171f, #fff)' },
  ADE: { name: 'Adelaide', short: 'Crows', color: '#002b5c', textColor: '#ffd204', gradient: 'linear-gradient(135deg, #002b5c, #ed1c24)' },
  NTH: { name: 'North Melbourne', short: 'Roos', color: '#013b9f', textColor: '#fff', gradient: 'linear-gradient(135deg, #013b9f, #fff)' },
  NOR: { name: 'North Melbourne', short: 'Kangaroos', color: '#013b9f', textColor: '#fff', gradient: 'linear-gradient(135deg, #013b9f, #fff)' },
  WBD: { name: 'Western Bulldogs', short: 'Bulldogs', color: '#014896', textColor: '#fff', gradient: 'linear-gradient(135deg, #014896, #ed1c24)' },
  PTA: { name: 'Port Adelaide', short: 'Power', color: '#008aab', textColor: '#fff', gradient: 'linear-gradient(135deg, #008aab, #000)' },
  GCS: { name: 'Gold Coast', short: 'Suns', color: '#eb003b', textColor: '#fff', gradient: 'linear-gradient(135deg, #eb003b, #ffd204)' },
  // Lumen8 Finals — event portal, Lumen8 brand colours
  FIN: { name: 'Lumen8 Finals', short: 'Finals', color: '#22BA91', textColor: '#fff', gradient: 'linear-gradient(135deg, #22BA91, #141414)' },
};

const VENUES = {
  MCG: { name: 'MCG', fullName: 'Melbourne Cricket Ground' },
  MRV: { name: 'Marvel Stadium', fullName: 'Marvel Stadium' },
  MARVEL: { name: 'Marvel Stadium', fullName: 'Marvel Stadium' },
};

// Get club name from environment
const ENV_CLUB = process.env.REACT_APP_DEFAULT_CLUB || 'STK';
const CURRENT_CLUB = CLUBS[ENV_CLUB] || CLUBS.STK;

// ============================================================
// HELPERS
// ============================================================
function getAESTTime() {
  const now = new Date();
  return new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));
}

function parseFixtureName(viewName) {
  if (!viewName) return null;
  const name = String(viewName).toUpperCase();
  
  const roundMatch = name.match(/RD?(\d+)/i) || name.match(/ROUND\s*(\d+)/i);
  const round = roundMatch ? parseInt(roundMatch[1], 10) : null;
  
  let venue = null;
  for (const [code, data] of Object.entries(VENUES)) {
    if (name.includes(`_${code}`) || name.includes(`${code}_`) || name.endsWith(code)) {
      venue = { code, ...data };
      break;
    }
  }
  
  const teamCodes = [];
  const parts = name.split(/[_\s]+/);
  for (const part of parts) {
    if (part === 'V' || part === 'VS' || part === 'OR' || part === 'IR') continue;
    if (CLUBS[part]) teamCodes.push(part);
  }
  
  const homeTeam = teamCodes[0] ? { code: teamCodes[0], ...CLUBS[teamCodes[0]] } : null;
  const awayTeam = teamCodes[1] ? { code: teamCodes[1], ...CLUBS[teamCodes[1]] } : null;
  
  return { raw: viewName, round, venue, homeTeam, awayTeam, hasTeams: !!(homeTeam && awayTeam) };
}

function getCountdown(targetDate) {
  const now = new Date();
  const target = new Date(targetDate);
  const diff = target - now;
  
  if (diff <= 0 && diff > -86400000) return { isGameDay: true, isPast: false, days: 0, hours: 0, minutes: 0, seconds: 0 };
  if (diff <= -86400000) return { isGameDay: false, isPast: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
    isGameDay: false,
    isPast: false,
  };
}

// ============================================================
// WELCOME HERO - Lumen8 Teal Ombre (Top of page)
// ============================================================
export function WelcomeHero({ userName }) {
  const firstName = userName?.split(' ')[0];
  
  return (
    <div style={styles.heroContainer}>
      <div style={styles.heroGlow} />
      <div style={styles.heroContent}>
        <div style={styles.heroSubtitle}>WELCOME BACK, {CURRENT_CLUB.name.toUpperCase()}</div>
        <h1 style={styles.heroTitle}>
          Welcome to VenueFlow{firstName ? ', ' : ''}
          {firstName && <span style={styles.heroName}>{firstName}</span>}
        </h1>
        <p style={styles.heroDesc}>
          Lumen8 VenueFlow is your central hub for uploading, scheduling, and managing event & match-day content across venues.
        </p>
      </div>
    </div>
  );
}

// ============================================================
// RECENT UPLOADS WIDGET - Last 3 uploads
// ============================================================
export function RecentUploadsWidget({ recentUploads = [] }) {
  const getFileIcon = (title) => {
    const name = (title || '').toLowerCase();
    if (name.includes('.mp4') || name.includes('.mov') || name.includes('video')) return '🎬';
    if (name.includes('.png') || name.includes('.jpg') || name.includes('image')) return '🖼️';
    if (name.includes('.gif')) return '✨';
    return '📁';
  };
  
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffHrs < 1) return 'Just now';
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };
  
  const getUploaderName = (email) => {
    if (!email) return 'Unknown';
    // Extract name from email: john.smith@club.com -> John S.
    const localPart = email.split('@')[0];
    const parts = localPart.split(/[._-]/);
    if (parts.length >= 2) {
      const firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
      const lastInitial = parts[1].charAt(0).toUpperCase() + '.';
      return `${firstName} ${lastInitial}`;
    }
    return localPart.charAt(0).toUpperCase() + localPart.slice(1);
  };
  
  return (
    <div className="wdgt-fade wdgt-delay-2" style={styles.uploadsCard}>
      <div style={styles.cardHeader}>
        <span style={styles.cardTitle}>
          <span style={{ fontSize: 16 }}>📤</span>
          Recent Uploads
        </span>
        <span style={styles.uploadCount}>{recentUploads.length} new</span>
      </div>
      
      {recentUploads.length > 0 ? (
        <div style={styles.uploadsList}>
          {recentUploads.slice(0, 3).map((upload, idx) => (
            <div 
              key={upload.id || idx} 
              style={{
                ...styles.uploadItem,
                animation: `wdgtSlideIn 0.3s ease-out ${idx * 0.05}s forwards`,
                opacity: 0,
              }}
            >
              <div style={styles.uploadThumb}>
                {getFileIcon(upload.title)}
              </div>
              <div style={styles.uploadContent}>
                <div style={styles.uploadName}>{upload.title || 'Untitled'}</div>
                <div style={styles.uploadMeta}>
                  <span style={styles.uploadUser}>{getUploaderName(upload.submitterEmail)}</span>
                  <span style={styles.uploadTime}>{formatTime(upload.createdTime)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.emptyState}>
          <span style={{ fontSize: 24, opacity: 0.5 }}>📭</span>
          <span style={{ color: THEME.textMuted, fontSize: 12 }}>No recent uploads</span>
        </div>
      )}
    </div>
  );
}

// ============================================================
// NEXT MATCH WIDGET
// ============================================================
export function NextMatchWidget({ fixtures = [], onNavigate }) {
  const [time, setTime] = useState(new Date());
  const [countdown, setCountdown] = useState(null);
  
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  // Find next match from fixtures
  const nextMatch = useMemo(() => {
    if (!fixtures || fixtures.length === 0) return null;
    
    // Try fixtures with dateTime first (from fetchUpcomingFixtures)
    for (const f of fixtures) {
      const matchId = f.matchId || f.viewName || f.fixtureKey;
      if (!matchId) continue;
      
      const parsed = parseFixtureName(matchId);
      if (parsed?.hasTeams) {
        return {
          ...f,
          parsed,
          dateTime: f.dateTime || null,
          venue: f.venue || parsed.venue?.name,
          round: f.round || parsed.round,
        };
      }
    }
    
    // Fallback: first fixture
    const first = fixtures[0];
    if (!first) return null;
    const matchId = first.matchId || first.viewName || first.fixtureKey;
    const parsed = parseFixtureName(matchId);
    return { ...first, parsed };
  }, [fixtures]);
  
  // Calculate match date
  const matchDate = useMemo(() => {
    if (nextMatch?.dateTime) {
      return new Date(nextMatch.dateTime);
    }
    // Estimate next Saturday 7:25 PM AEST
    const aestNow = getAESTTime();
    const daysUntilSat = (6 - aestNow.getDay() + 7) % 7 || 7;
    const sat = new Date(aestNow);
    sat.setDate(aestNow.getDate() + daysUntilSat);
    sat.setHours(19, 25, 0, 0);
    return sat;
  }, [nextMatch]);
  
  // Update countdown
  useEffect(() => {
    setCountdown(getCountdown(matchDate));
  }, [matchDate, time]);
  
  const isGameDay = countdown?.isGameDay;
  const p = nextMatch?.parsed;
  
  // Format match date for display
  const matchDateStr = useMemo(() => {
    if (!nextMatch?.dateTime) return null;
    const d = new Date(nextMatch.dateTime);
    return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) +
           ' • ' + d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  }, [nextMatch]);
  
  if (!p?.hasTeams) {
    return (
      <div className="wdgt-fade wdgt-delay-1" style={styles.matchCard}>
        <div style={styles.cardHeader}>
          <span style={styles.cardTitle}>Next Match</span>
        </div>
        <div style={styles.emptyState}>
          <span style={{ fontSize: 32 }}>🏟️</span>
          <span style={{ color: THEME.textMuted, fontSize: 13 }}>No upcoming matches found</span>
          <span style={{ color: THEME.textMuted, fontSize: 11 }}>Check your Fixtures table in Airtable</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="wdgt-fade wdgt-delay-1" style={{
      ...styles.matchCard,
      ...(isGameDay ? styles.matchCardGameDay : {}),
    }}>
      {/* Game Day Banner */}
      {isGameDay && (
        <div style={styles.gameDayBanner}>
          🎉 IT'S GAME DAY! 🎉
        </div>
      )}
      
      <div style={{ ...styles.cardHeader, marginTop: isGameDay ? 36 : 0 }}>
        <span style={styles.cardTitle}>Next Match</span>
        {p.round && <span style={styles.roundBadge}>Round {p.round}</span>}
      </div>
      
      {/* Teams */}
      <div style={styles.teamsRow}>
        <div style={styles.teamBlock}>
          <div style={{ ...styles.teamBadge, background: p.homeTeam?.gradient || p.homeTeam?.color }}>
            <span style={{ color: p.homeTeam?.textColor }}>{p.homeTeam?.code}</span>
          </div>
          <span style={styles.teamName}>{p.homeTeam?.short || p.homeTeam?.name}</span>
        </div>
        
        <div style={styles.vsBlock}>
          <span style={styles.vsText}>VS</span>
        </div>
        
        <div style={styles.teamBlock}>
          <div style={{ ...styles.teamBadge, background: p.awayTeam?.gradient || p.awayTeam?.color }}>
            <span style={{ color: p.awayTeam?.textColor }}>{p.awayTeam?.code}</span>
          </div>
          <span style={styles.teamName}>{p.awayTeam?.short || p.awayTeam?.name}</span>
        </div>
      </div>
      
      {/* Venue & Date */}
      <div style={styles.matchMeta}>
        {(nextMatch.venue || p.venue) && (
          <span>📍 {nextMatch.venue || p.venue?.fullName || p.venue?.name}</span>
        )}
        {matchDateStr && <span style={styles.matchDate}>🗓️ {matchDateStr}</span>}
      </div>
      
      {/* Countdown */}
      {!isGameDay && countdown && !countdown.isPast && (
        <div style={styles.countdownRow}>
          <div style={styles.countdownItem}>
            <span style={styles.countdownNum}>{countdown.days}</span>
            <span style={styles.countdownLabel}>days</span>
          </div>
          <span style={styles.countdownSep}>:</span>
          <div style={styles.countdownItem}>
            <span style={styles.countdownNum}>{String(countdown.hours).padStart(2, '0')}</span>
            <span style={styles.countdownLabel}>hrs</span>
          </div>
          <span style={styles.countdownSep}>:</span>
          <div style={styles.countdownItem}>
            <span style={styles.countdownNum}>{String(countdown.minutes).padStart(2, '0')}</span>
            <span style={styles.countdownLabel}>min</span>
          </div>
          <span style={styles.countdownSep}>:</span>
          <div style={styles.countdownItem}>
            <span style={{ ...styles.countdownNum, color: THEME.accent }}>{String(countdown.seconds).padStart(2, '0')}</span>
            <span style={styles.countdownLabel}>sec</span>
          </div>
        </div>
      )}
      
      {/* CTA */}
      <button style={styles.matchCta} onClick={() => onNavigate?.('/matchflow')}>
        {isGameDay ? '🚀 Launch MatchFlow Now' : 'Prepare Match Content →'}
      </button>
    </div>
  );
}

// ============================================================
// MATCHFLOW BUILDER PIPELINE WIDGET - From Operator Content Status
// ============================================================
export function PipelineWidget({ stats = {} }) {
  const [animatedStats, setAnimatedStats] = useState({ newSubmission: 0, rejected: 0, approved: 0 });
  
  const pipelineStats = {
    newSubmission: stats.newSubmission ?? 0,
    rejected: stats.rejected ?? 0,
    approved: stats.approved ?? 0,
  };
  
  useEffect(() => {
    const duration = 800;
    const steps = 25;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const ease = 1 - Math.pow(1 - step / steps, 3);
      setAnimatedStats({
        newSubmission: Math.round(pipelineStats.newSubmission * ease),
        rejected: Math.round(pipelineStats.rejected * ease),
        approved: Math.round(pipelineStats.approved * ease),
      });
      if (step >= steps) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [pipelineStats.newSubmission, pipelineStats.rejected, pipelineStats.approved]);
  
  const stages = [
    { key: 'newSubmission', label: 'New', color: THEME.orange, icon: '📋' },
    { key: 'rejected', label: 'Rejected', color: THEME.red, icon: '❌' },
    { key: 'approved', label: 'Approved', color: THEME.accent, icon: '✅' },
  ];
  
  const total = pipelineStats.newSubmission + pipelineStats.rejected + pipelineStats.approved;
  
  return (
    <div className="wdgt-fade wdgt-delay-3" style={styles.pipelineCard}>
      <div style={styles.cardHeader}>
        <span style={styles.cardTitle}>MatchFlow Builder Pipeline</span>
        <span style={styles.pipelineTotal}>{total} total</span>
      </div>
      
      <div style={styles.pipelineStages}>
        {stages.map((stage, idx) => (
          <div key={stage.key} style={styles.pipelineStageRow}>
            <div style={{ ...styles.pipelineBadge, background: `${stage.color}20`, borderColor: stage.color }}>
              <span style={{ fontSize: 16 }}>{stage.icon}</span>
              <span style={{ ...styles.pipelineCount, color: stage.color }}>{animatedStats[stage.key]}</span>
            </div>
            <span style={styles.pipelineStageName}>{stage.label}</span>
            {idx < stages.length - 1 && <span style={styles.pipelineFlowArrow}>→</span>}
          </div>
        ))}
      </div>
      
      {pipelineStats.newSubmission > 0 && (
        <div style={styles.pipelineAlert}>
          ⚡ {pipelineStats.newSubmission} new submission{pipelineStats.newSubmission > 1 ? 's' : ''} to review
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN DASHBOARD - Combines Header + Widgets
// ============================================================
export function WelcomeDashboard({ fixtures = [], pipelineStats = {}, recentUploads = [], onNavigate }) {
  return (
    <div style={styles.dashboard}>
      {/* Widget Grid - 2 columns */}
      <div style={styles.widgetGridTwo}>
        <RecentUploadsWidget recentUploads={recentUploads} />
        <PipelineWidget stats={pipelineStats} />
      </div>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  dashboard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  
  // Hero Section (top of page)
  heroContainer: {
    textAlign: 'center',
    marginBottom: 36,
    position: 'relative',
  },
  heroGlow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    height: 400,
    background: 'radial-gradient(ellipse, rgba(47, 214, 181, 0.08) 0%, transparent 60%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
  },
  heroSubtitle: {
    fontSize: 13,
    fontWeight: 600,
    color: THEME.accent,
    letterSpacing: '2px',
    marginBottom: 8,
  },
  heroTitle: {
    fontSize: 38,
    fontWeight: 700,
    color: THEME.textPrimary,
    letterSpacing: '-1px',
    marginBottom: 12,
    margin: 0,
  },
  heroName: {
    background: LUMEN8_TEAL_GRADIENT,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  heroDesc: {
    fontSize: 15,
    color: THEME.textSecondary,
    maxWidth: 520,
    lineHeight: 1.6,
    margin: '0 auto',
  },
  
  // Widget Grid
  widgetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
  },
  widgetGridTwo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 20,
  },
  
  // Card base
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: THEME.textPrimary,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  
  // Uploads Card
  uploadsCard: {
    background: THEME.cardBg,
    borderRadius: 16,
    border: `1px solid ${THEME.cardBorder}`,
    padding: 24,
    minHeight: 180,
    minWidth: 0,
    overflow: 'hidden',
  },
  uploadCount: {
    fontSize: 12,
    color: THEME.accent,
    fontWeight: 600,
  },
  uploadsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  uploadItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    background: THEME.glassBg,
    borderRadius: 10,
  },
  uploadThumb: {
    width: 40,
    height: 40,
    borderRadius: 8,
    background: `linear-gradient(135deg, ${THEME.accent}20, ${THEME.accentAlt}20)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    flexShrink: 0,
  },
  uploadContent: {
    flex: 1,
    minWidth: 0,
  },
  uploadName: {
    fontSize: 13,
    fontWeight: 600,
    color: THEME.textPrimary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginBottom: 2,
  },
  uploadMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 11,
  },
  uploadUser: {
    color: THEME.textSecondary,
  },
  uploadTime: {
    color: THEME.textMuted,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 24,
    minHeight: 120,
  },
  
  // Match Card
  matchCard: {
    background: THEME.cardBg,
    borderRadius: 16,
    border: `1px solid ${THEME.cardBorder}`,
    padding: 20,
    position: 'relative',
    overflow: 'hidden',
    minHeight: 200,
  },
  matchCardGameDay: {
    border: `2px solid ${THEME.accent}`,
    animation: 'wdgtGlow 2s ease-in-out infinite',
  },
  gameDayBanner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '8px 0',
    background: `linear-gradient(90deg, ${THEME.accent}, ${THEME.accentAlt})`,
    color: '#000',
    fontSize: 11,
    fontWeight: 800,
    textAlign: 'center',
    letterSpacing: 1,
  },
  roundBadge: {
    fontSize: 11,
    fontWeight: 600,
    color: THEME.textSecondary,
    background: THEME.glassBg,
    padding: '4px 10px',
    borderRadius: 6,
  },
  teamsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginBottom: 12,
  },
  teamBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  teamBadge: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 900,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  teamName: {
    fontSize: 11,
    fontWeight: 600,
    color: THEME.textSecondary,
    textAlign: 'center',
  },
  vsBlock: {
    padding: '0 8px',
  },
  vsText: {
    fontSize: 10,
    fontWeight: 800,
    color: THEME.textMuted,
    background: THEME.glassBg,
    padding: '4px 8px',
    borderRadius: 4,
  },
  matchMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: THEME.textSecondary,
    marginBottom: 12,
  },
  matchDate: {
    color: THEME.accent,
    fontWeight: 600,
  },
  countdownRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginBottom: 14,
  },
  countdownItem: {
    background: THEME.glassBg,
    borderRadius: 8,
    padding: '8px 10px',
    textAlign: 'center',
    minWidth: 42,
  },
  countdownNum: {
    fontSize: 18,
    fontWeight: 900,
    color: THEME.textPrimary,
    fontFamily: 'SF Mono, Monaco, monospace',
    display: 'block',
    lineHeight: 1,
  },
  countdownLabel: {
    fontSize: 8,
    fontWeight: 600,
    color: THEME.textMuted,
    letterSpacing: 0.5,
    marginTop: 2,
    display: 'block',
  },
  countdownSep: {
    fontSize: 16,
    fontWeight: 700,
    color: THEME.textMuted,
    animation: 'wdgtPulse 1s ease-in-out infinite',
  },
  matchCta: {
    width: '100%',
    padding: '10px 16px',
    fontSize: 12,
    fontWeight: 700,
    color: '#000',
    background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentAlt})`,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
  },
  
  // Pipeline Card
  pipelineCard: {
    background: THEME.cardBg,
    borderRadius: 16,
    border: `1px solid ${THEME.cardBorder}`,
    padding: 24,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
  },
  pipelineTotal: {
    fontSize: 12,
    color: THEME.textMuted,
    fontWeight: 500,
  },
  pipelineStages: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    flex: 1,
    marginBottom: 16,
  },
  pipelineStageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  pipelineBadge: {
    width: 64,
    height: 64,
    borderRadius: 14,
    border: '2px solid',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  pipelineCount: {
    fontSize: 20,
    fontWeight: 900,
    fontFamily: 'SF Mono, Monaco, monospace',
    lineHeight: 1,
  },
  pipelineStageName: {
    fontSize: 12,
    fontWeight: 600,
    color: THEME.textSecondary,
    minWidth: 60,
  },
  pipelineFlowArrow: {
    color: THEME.textMuted,
    fontSize: 16,
    marginLeft: 4,
    marginRight: 4,
  },
  pipelineAlert: {
    fontSize: 12,
    color: THEME.orange,
    fontWeight: 600,
    padding: '10px 16px',
    background: `${THEME.orange}15`,
    borderRadius: 10,
    textAlign: 'center',
  },
};

export default WelcomeDashboard;
